import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, assetSrc } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ArrowLeft, FloppyDisk, X, Package, MagnifyingGlass, FilePdf, Plus, Trash, ArrowSquareOut, Eye, UploadSimple, Warning } from "@phosphor-icons/react";

const LABEL = "mb-1.5 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";
const INPUT = "w-full border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200";

function flatten(cats, depth = 0, parent = "", out = []) {
  (cats || []).forEach((c) => {
    out.push({ id: String(c.category_id), name: c.name?.fr || c.name?.en || "", parent, depth });
    if (c.children) flatten(c.children, depth + 1, c.name?.fr || c.name?.en || "", out);
  });
  return out;
}

const pdfFileBase = (url) => {
  if (!url) return "";
  try { return decodeURIComponent(url.split("/").pop().split("?")[0]).replace(/\.pdf$/i, ""); } catch { return ""; }
};
const normPdfEntry = (e) => {
  const url = typeof e === "string" ? e : (e?.url || "");
  const base = pdfFileBase(url);
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(base);
  const fallback = isUuid ? "" : base;
  return { url, name_fr: (typeof e === "object" && e?.name_fr) || fallback, name_en: (typeof e === "object" && e?.name_en) || fallback };
};


// Small product search box (used by Similar products + Add variant)
const ProductSearch = ({ testid, onPick, excludeIds = [] }) => {
  const { t, lang } = useLang();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const tm = setTimeout(() => {
      axios.get(`${API}/admin/products`, { params: { q, per_page: 12 } })
        .then((r) => setResults((r.data.items || []).filter((x) => !excludeIds.includes(x.product_id))))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(tm);
  }, [q, excludeIds]);
  return (
    <div className="relative">
      <div className="flex items-center border border-border bg-background px-3 focus-within:border-primary transition-colors duration-200">
        <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground" />
        <input data-testid={testid} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder={t.searchProduct} className="w-full bg-transparent px-2 py-2.5 text-sm outline-none placeholder-muted-foreground" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto border border-border bg-card shadow-lg">
          {results.map((r) => (
            <button key={r.product_id} data-testid={`${testid}-opt-${r.product_id}`} onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(r); setQ(""); setResults([]); }}
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-primary hover:text-white transition-colors duration-100">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">
                {r.image ? <img src={assetSrc(r.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={14} className="text-muted-foreground" />}
              </div>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{r.name?.[lang] || r.name?.fr}</span>
                <span className="block truncate font-mono text-[11px] opacity-70">{r.product_code}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProductEditPage() {
  const { productId } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [catQuery, setCatQuery] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [tab, setTab] = useState("general");
  const [similar, setSimilar] = useState([]); // resolved display list
  const [similarDirty, setSimilarDirty] = useState(false);
  const [similarPending, setSimilarPending] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [variations, setVariations] = useState(null);
  const imgFileRef = useRef(null);
  const pdfFileRef = useRef(null);
  const [imgUploading, setImgUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(null); // `${list}-${i}` while uploading
  const pdfTargetRef = useRef(null); // { list, i }
  const [showDel, setShowDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newVar, setNewVar] = useState({ product_code: "", variant_value: "", price: "" });
  const [creatingVar, setCreatingVar] = useState(false);
  const [delVar, setDelVar] = useState(null);
  const [deletingVar, setDeletingVar] = useState(false);

  const deleteProduct = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success(t.productDeleted);
      navigate("/admin/products");
    } catch (e) { toast.error(e.response?.data?.detail || t.deleteFailed); setDeleting(false); }
  };

  useEffect(() => {
    axios.get(`${API}/admin/products/${productId}`).then((r) => {
      const d = r.data;
      setP({
        product_code: d.product_code || "",
        name: { fr: d.name?.fr || "", en: d.name?.en || "" },
        description: { fr: d.description?.fr || "", en: d.description?.en || "" },
        category_ids: (d.category_ids || []).map(String),
        price: d.price ?? 0,
        quantity: d.quantity ?? 0,
        image: d.image || "",
        status: d.status || "A",
        tax_gst: !!d.tax_gst,
        tax_qst: !!d.tax_qst,
        pdfs_fr: (d.pdfs_fr || []).map(normPdfEntry),
        pdfs_en: (d.pdfs_en || []).map(normPdfEntry),
        similar_ids: (d.similar_ids || []).map(String),
      });
      setSimilar(d.similar || []);
      setSimilarPending(!!d.similar_pending);
    }).catch(() => setP(false));
    axios.get(`${API}/shop/categories`).then((r) => setCats(flatten(r.data))).catch(() => {});
  }, [productId]);

  // Rafraichit la liste quand l'IA termine la generation en arriere-plan
  useEffect(() => {
    if (!similarPending) return;
    const iv = setInterval(() => {
      axios.get(`${API}/admin/products/${productId}`).then(({ data }) => {
        if (data.similar_pending) return;
        setSimilarPending(false);
        if (!similarDirty) {
          setP((o) => (o ? { ...o, similar_ids: (data.similar_ids || []).map(String) } : o));
          setSimilar(data.similar || []);
          if ((data.similar || []).length) toast.success(lang === "fr" ? "Produits similaires ajoutés par l'IA" : "Similar products added by AI");
        }
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(iv);
  }, [similarPending, similarDirty, productId, lang]);

  const regenerateSimilar = async () => {
    setRegenLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/products/${productId}/similar/regenerate`);
      set({ similar_ids: (data.similar_ids || []).map(String) });
      setSimilar(data.similar || []);
      setSimilarDirty(false);
      toast.success(lang === "fr" ? "Produits similaires régénérés" : "Similar products regenerated");
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setRegenLoading(false); }
  };

  // Lazy-load variations when the tab is first opened
  useEffect(() => {
    if (tab === "variations" && variations === null) {
      axios.get(`${API}/admin/products/${productId}/variations`).then((r) => setVariations(r.data)).catch(() => setVariations({ variation_group_id: 0, variation_label: "", variants: [] }));
    }
  }, [tab, variations, productId]);

  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const set = (patch) => setP((o) => ({ ...o, ...patch }));
  const addCat = (id) => { if (id && !p.category_ids.includes(id)) set({ category_ids: [...p.category_ids, id] }); setCatQuery(""); };
  const removeCat = (id) => set({ category_ids: p.category_ids.filter((c) => c !== id) });
  const setPdfField = (list, i, field, value) => set({ [list]: p[list].map((x, idx) => (idx === i ? { ...x, [field]: value } : x)) });
  const addPdfRow = (list) => set({ [list]: [...p[list], { url: "", name_fr: "", name_en: "" }] });
  const removePdfRow = (list, i) => set({ [list]: p[list].filter((_, idx) => idx !== i) });

  const onUploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/uploads/image`, fd);
      set({ image: data.url });
      toast.success(t.saved);
    } catch (err) { toast.error(err.response?.data?.detail || t.saveFailed); }
    finally { setImgUploading(false); if (imgFileRef.current) imgFileRef.current.value = ""; }
  };

  const triggerPdfUpload = (list, i) => { pdfTargetRef.current = { list, i }; pdfFileRef.current?.click(); };
  const onUploadPdf = async (e) => {
    const f = e.target.files?.[0];
    const target = pdfTargetRef.current;
    if (!f || !target) return;
    const { list, i } = target;
    setPdfUploading(`${list}-${i}`);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/uploads/pdf`, fd);
      const base = (data.filename || "").replace(/\.pdf$/i, "").trim();
      set({ [list]: p[list].map((x, idx) => (idx === i ? { ...x, url: data.url, name_fr: x.name_fr || base, name_en: x.name_en || base } : x)) });
      toast.success(t.pdfUploadedClickSave);
    } catch (err) { toast.error(err.response?.data?.detail || t.saveFailed); }
    finally { setPdfUploading(null); pdfTargetRef.current = null; if (pdfFileRef.current) pdfFileRef.current.value = ""; }
  };

  const addSimilar = (r) => {
    if (p.similar_ids.includes(r.product_id) || r.product_id === productId) return;
    set({ similar_ids: [...p.similar_ids, r.product_id] });
    setSimilarDirty(true);
    setSimilar((s) => [...s, { product_id: r.product_id, product_code: r.product_code, name: r.name, image: r.image }]);
  };
  const removeSimilar = (pid) => {
    set({ similar_ids: p.similar_ids.filter((x) => x !== pid) });
    setSimilarDirty(true);
    setSimilar((s) => s.filter((x) => x.product_id !== pid));
  };

  const filteredCats = useMemo(() => {
    const qq = catQuery.trim().toLowerCase();
    return cats.filter((c) => !p?.category_ids.includes(c.id) &&
      (qq === "" || c.name.toLowerCase().includes(qq) || (c.parent || "").toLowerCase().includes(qq))).slice(0, 80);
  }, [cats, catQuery, p]);

  const save = async () => {
    if (!p.name.fr.trim()) { toast.error(t.nameFr + " *"); return false; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${productId}`, {
        product_code: p.product_code,
        name_fr: p.name.fr, name_en: p.name.en,
        description_fr: p.description.fr, description_en: p.description.en,
        category_ids: p.category_ids, main_category: p.category_ids[0] || null,
        price: parseFloat(p.price) || 0, quantity: parseInt(p.quantity, 10) || 0,
        image: p.image, status: p.status, tax_gst: p.tax_gst, tax_qst: p.tax_qst,
        pdfs_fr: p.pdfs_fr.filter((x) => x.url), pdfs_en: p.pdfs_en.filter((x) => x.url),
        ...(similarDirty ? { similar_ids: p.similar_ids } : {}),
      });
      setSimilarDirty(false);
      toast.success(t.productUpdated);
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); return false; }
    finally { setSaving(false); }
  };
  const saveAndClose = async () => { if (await save()) navigate("/admin/products"); };

  // Variations mutations
  const setVarLabel = async (body) => {
    try { const r = await axios.put(`${API}/admin/products/${productId}/variation-label`, body); setVariations(r.data); toast.success(t.saved); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
  };
  const updateVariant = async (vid, body) => {
    try { const r = await axios.put(`${API}/admin/products/${productId}/variations/${vid}`, body); setVariations(r.data); toast.success(t.saved); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
  };
  const removeVariant = (v) => setDelVar(v);
  const confirmDeleteVariant = async () => {
    if (!delVar) return;
    setDeletingVar(true);
    try {
      const r = await axios.delete(`${API}/admin/products/${productId}/variations/${delVar.product_id}`);
      setVariations(r.data);
      setDelVar(null);
      toast.success(t.variationDeleted);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setDeletingVar(false); }
  };
  const createVariant = async () => {
    if (!newVar.product_code.trim()) { toast.error(t.productCode + " *"); return; }
    setCreatingVar(true);
    try {
      const res = await axios.post(`${API}/admin/products/${productId}/variations/new`, {
        product_code: newVar.product_code.trim(), variant_value: newVar.variant_value.trim(), price: parseFloat(newVar.price) || 0,
      });
      setVariations(res.data);
      setNewVar({ product_code: "", variant_value: "", price: "" });
      toast.success(t.variantCreated);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setCreatingVar(false); }
  };

  if (p === null) return <div className="min-h-screen bg-background"><AppHeader /><div className="p-8 text-muted-foreground">{t.loading}</div></div>;
  if (p === false) return <div className="min-h-screen bg-background"><AppHeader /><div className="p-8 text-muted-foreground">{t.noResults}</div></div>;

  const Actions = () => (
    <div className="flex flex-wrap items-center gap-3">
      <button data-testid="save-button" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
        <FloppyDisk size={16} weight="bold" /> {t.save}
      </button>
      <button data-testid="save-close-button" onClick={saveAndClose} disabled={saving}
        className="inline-flex items-center gap-2 border border-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-primary hover:bg-primary hover:text-white transition-colors duration-200 disabled:opacity-60">
        {t.saveAndClose}
      </button>
      <button data-testid="preview-button" onClick={() => window.open(`/product/${productId}`, "_blank")}
        className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200">
        <Eye size={16} weight="bold" /> {t.preview}
      </button>
      <button data-testid="cancel-button" onClick={() => navigate("/admin/products")}
        className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200">
        {t.cancel}
      </button>
      <button data-testid="delete-product-button" onClick={() => setShowDel(true)}
        className="inline-flex items-center gap-2 border border-red-400 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-red-500 hover:bg-red-500 hover:text-white transition-colors duration-200">
        <Trash size={16} weight="bold" /> {t.deleteProduct}
      </button>
    </div>
  );

  const tabs = [
    { key: "general", label: t.general },
    { key: "similar", label: t.similarProducts },
    { key: "variations", label: t.variations },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        <button data-testid="back-to-products" onClick={() => navigate("/admin/products")}
          className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors duration-200">
          <ArrowLeft size={16} weight="bold" /> {t.productsManagement}
        </button>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
          <h1 data-testid="edit-title" className="font-display text-2xl font-black tracking-tighter text-foreground">{p.name[lang] || p.name.fr || t.editProduct}</h1>
          <Actions />
        </div>

        <div className="mb-6 flex flex-wrap gap-1 border-b border-border" data-testid="product-subtabs">
          {tabs.map((tb) => (
            <button key={tb.key} data-testid={`product-tab-${tb.key}`} onClick={() => setTab(tb.key)}
              className={`-mb-px border-b-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${tab === tb.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "general" && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground" data-testid="lang-banner">
            <span className="font-bold uppercase tracking-wider text-primary">{t.editingLanguage}: {lang === "fr" ? "Français" : "English"}</span>
            <span className="text-muted-foreground">— {lang === "fr" ? "Utilisez FR / EN en haut à droite." : "Use FR / EN at the top right."}</span>
          </div>

          <label className="block">
            <span className={LABEL}>{t.name} ({lang.toUpperCase()}) *</span>
            <input data-testid="field-name" className={INPUT} value={p.name[lang]}
              onChange={(e) => set({ name: { ...p.name, [lang]: e.target.value } })} />
          </label>

          <div>
            <span className={LABEL}>{t.categories} *</span>
            <div className="mb-2 flex flex-wrap gap-2" data-testid="category-chips">
              {p.category_ids.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
              {p.category_ids.map((id) => {
                const c = catById[id];
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 border border-border bg-muted px-2.5 py-1 text-xs text-foreground">
                    <span className="font-semibold">{c?.name || id}</span>
                    {c?.parent && <span className="text-muted-foreground">{c.parent}</span>}
                    <button data-testid={`remove-cat-${id}`} onClick={() => removeCat(id)} className="text-muted-foreground hover:text-primary"><X size={12} weight="bold" /></button>
                  </span>
                );
              })}
            </div>
            <div className="relative">
              <div className="flex items-center border border-border bg-background px-3 focus-within:border-primary transition-colors duration-200">
                <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground" />
                <input data-testid="category-search" value={catQuery}
                  onChange={(e) => { setCatQuery(e.target.value); setCatOpen(true); }}
                  onFocus={() => setCatOpen(true)} onBlur={() => setTimeout(() => setCatOpen(false), 150)}
                  placeholder={t.searchCategory} className="w-full bg-transparent px-2 py-2.5 text-sm outline-none placeholder-muted-foreground" />
              </div>
              {catOpen && filteredCats.length > 0 && (
                <div data-testid="category-dropdown" className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto border border-border bg-card shadow-lg">
                  {filteredCats.map((c) => (
                    <button key={c.id} data-testid={`cat-option-${c.id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => addCat(c.id)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary hover:text-white transition-colors duration-100">
                      <span className="font-medium">{c.name}</span>
                      {c.parent && <span className="text-xs text-muted-foreground">{c.parent}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <label><span className={LABEL}>{t.productCode}</span><input data-testid="field-code" className={`${INPUT} font-mono`} value={p.product_code} onChange={(e) => set({ product_code: e.target.value })} /></label>
            <label><span className={LABEL}>{t.price} ($)</span><input data-testid="field-price" type="number" step="0.01" min="0" className={`${INPUT} tabular-nums`} value={p.price} onChange={(e) => set({ price: e.target.value })} /></label>
            <label><span className={LABEL}>{t.stock}</span><input data-testid="field-quantity" type="number" min="0" className={`${INPUT} tabular-nums`} value={p.quantity} onChange={(e) => set({ quantity: e.target.value })} /></label>
          </div>

          <div>
            <span className={LABEL}>{t.descriptionFr.replace(" (FR)", "")} ({lang.toUpperCase()})</span>
            <RichTextEditor testid="field-description" value={p.description[lang]}
              onChange={(html) => set({ description: { ...p.description, [lang]: html } })} />
          </div>

          <div>
            <span className={LABEL}>{t.status}</span>
            <div className="flex flex-wrap gap-4" data-testid="status-radios">
              {[["A", t.statusActive], ["D", t.statusDisabled], ["H", t.statusHidden]].map(([v, lbl]) => (
                <label key={v} className="flex items-center gap-2 text-sm text-foreground">
                  <input type="radio" name="status" data-testid={`status-${v}`} checked={p.status === v} onChange={() => set({ status: v })} /> {lbl}
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-foreground"><input data-testid="field-tax-gst" type="checkbox" checked={p.tax_gst} onChange={(e) => set({ tax_gst: e.target.checked })} /> {t.taxGst}</label>
            <label className="flex items-center gap-2 text-sm text-foreground"><input data-testid="field-tax-qst" type="checkbox" checked={p.tax_qst} onChange={(e) => set({ tax_qst: e.target.checked })} /> {t.taxQst}</label>
          </div>

          <div>
            <span className={LABEL}>{t.images}</span>
            <div className="flex items-start gap-4">
              <div className="h-24 w-24 shrink-0 border border-border bg-muted">
                {p.image ? <img src={assetSrc(p.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Package size={24} /></div>}
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <input ref={imgFileRef} type="file" accept="image/*" className="hidden" data-testid="field-image-file" onChange={onUploadImage} />
                <button type="button" data-testid="field-image-upload" onClick={() => imgFileRef.current?.click()} disabled={imgUploading}
                  className="inline-flex items-center gap-1.5 bg-primary px-3 py-2 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                  <UploadSimple size={15} weight="bold" /> {imgUploading ? t.uploading : t.uploadImage}
                </button>
                <input data-testid="field-image" className={INPUT} placeholder={t.orUrl} value={p.image} onChange={(e) => set({ image: e.target.value })} />
              </div>
            </div>
          </div>

          {[["pdfs_fr", "PDF (FR)"], ["pdfs_en", "PDF (EN)"]].map(([list, lbl]) => (
            <div key={list} data-testid={`pdf-section-${list}`}>
              <span className={LABEL}>{lbl}</span>
              <div className="space-y-2">
                {(p[list] || []).map((row, i) => (
                  <div key={i} className="border border-border p-3 space-y-2" data-testid={`pdf-row-${list}-${i}`}>
                    <div className="flex items-center gap-2">
                      <FilePdf size={18} weight="fill" className="shrink-0 text-primary" />
                      {row.url && <a href={row.url} target="_blank" rel="noreferrer" className="shrink-0 text-xs text-primary hover:underline">↗</a>}
                      <input data-testid={`${list}-${i}`} className={INPUT} placeholder="https://...pdf" value={row.url} onChange={(e) => setPdfField(list, i, "url", e.target.value)} />
                      <button type="button" data-testid={`${list}-upload-${i}`} onClick={() => triggerPdfUpload(list, i)} disabled={pdfUploading === `${list}-${i}`}
                        className="inline-flex shrink-0 items-center gap-1 border border-primary px-2.5 py-2 text-[10px] font-bold uppercase tracking-wider text-primary hover:bg-primary hover:text-white transition-colors duration-200 disabled:opacity-60" title={t.uploadPdf}>
                        <UploadSimple size={14} weight="bold" /> {pdfUploading === `${list}-${i}` ? t.uploading : t.uploadPdf}
                      </button>
                      <button onClick={() => removePdfRow(list, i)} className="text-muted-foreground hover:text-primary"><X size={16} weight="bold" /></button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 pl-7 sm:grid-cols-2">
                      <label className="block">
                        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.pdfNameFr}</span>
                        <input data-testid={`${list}-name-fr-${i}`} className={INPUT} placeholder="ex. Fiche technique — Détergent (FR)" value={row.name_fr || ""} onChange={(e) => setPdfField(list, i, "name_fr", e.target.value)} />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.pdfNameEn}</span>
                        <input data-testid={`${list}-name-en-${i}`} className={INPUT} placeholder="e.g. Technical sheet — Detergent (EN)" value={row.name_en || ""} onChange={(e) => setPdfField(list, i, "name_en", e.target.value)} />
                      </label>
                    </div>
                  </div>
                ))}
                <button data-testid={`add-${list}`} onClick={() => addPdfRow(list)} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary hover:underline"><Plus size={14} weight="bold" /> {t.addPdf}</button>
              </div>
            </div>
          ))}

          <input ref={pdfFileRef} type="file" accept="application/pdf" className="hidden" data-testid="pdf-upload-input" onChange={onUploadPdf} />

          <div className="border-t border-border pt-6"><Actions /></div>
        </div>
        )}

        {tab === "similar" && (
          <div className="space-y-4" data-testid="similar-tab">
            <p className="text-sm text-muted-foreground">{t.similarProducts} — {lang === "fr" ? "sélectionnez manuellement les produits à afficher sur cette fiche." : "manually select the products to show on this page."}</p>
            <div className="flex flex-wrap items-center gap-3">
              {similarPending && (
                <span data-testid="similar-pending" className="inline-flex items-center gap-2 border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
                  {lang === "fr" ? "L'IA choisit les produits similaires… (la liste se mettra à jour toute seule)" : "AI is picking similar products… (list will update automatically)"}
                </span>
              )}
              <button data-testid="similar-regenerate" onClick={regenerateSimilar} disabled={regenLoading || similarPending}
                className="inline-flex items-center gap-2 border border-primary px-3 py-2 text-xs font-bold uppercase tracking-wider text-primary hover:bg-primary hover:text-white disabled:opacity-50">
                {regenLoading ? (lang === "fr" ? "Génération…" : "Generating…") : (lang === "fr" ? "Régénérer avec l'IA" : "Regenerate with AI")}
              </button>
            </div>
            <ProductSearch testid="similar-search" onPick={addSimilar} excludeIds={[productId, ...p.similar_ids]} />
            {similar.length === 0 ? (
              <div data-testid="similar-empty" className="flex flex-col items-center gap-4 border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
                <span>{t.noSimilar}</span>
                {!similarPending && (
                  <button data-testid="similar-empty-generate" onClick={regenerateSimilar} disabled={regenLoading}
                    className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-wider text-white shadow hover:opacity-90 disabled:opacity-60">
                    {regenLoading && <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                    {regenLoading ? (lang === "fr" ? "L'IA choisit 4 produits…" : "AI is picking 4 products…") : (lang === "fr" ? "Ajouter 4 produits similaires avec l'IA" : "Add 4 similar products with AI")}
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border border border-border" data-testid="similar-list">
                {similar.map((s) => (
                  <div key={s.product_id} data-testid={`similar-item-${s.product_id}`} className="flex items-center gap-3 p-2">
                    <a href={`/admin/products/${s.product_id}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`similar-open-${s.product_id}`}
                      className="group flex min-w-0 flex-1 items-center gap-3 hover:opacity-90" title={t.openProductPage}>
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">
                        {s.image ? <img src={assetSrc(s.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={16} className="text-muted-foreground" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground group-hover:text-primary group-hover:underline">
                          {s.name?.[lang] || s.name?.fr}
                          <ArrowSquareOut size={13} weight="bold" className="shrink-0 text-muted-foreground group-hover:text-primary" />
                        </div>
                        <div className="truncate font-mono text-[11px] text-primary">{s.product_code}</div>
                      </div>
                    </a>
                    <button data-testid={`similar-remove-${s.product_id}`} onClick={() => removeSimilar(s.product_id)} className="shrink-0 border border-red-400 p-1.5 text-red-500 hover:bg-red-500 hover:text-white"><Trash size={15} weight="bold" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-border pt-4"><Actions /></div>
          </div>
        )}

        {tab === "variations" && (
          <div className="space-y-5" data-testid="variations-tab">
            {variations === null ? (
              <div className="py-10 text-center text-muted-foreground">{t.loading}</div>
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-4">
                  <label className="flex-1 min-w-[200px]">
                    <span className={LABEL}>{t.variationLabelField} (FR)</span>
                    <input key={`lblfr-${variations.variation_label}`} data-testid="variation-label-fr"
                      defaultValue={variations.variation_label || ""}
                      placeholder="ex. Choix de formats" className={INPUT}
                      onBlur={(e) => { if (e.target.value !== (variations.variation_label || "")) setVarLabel({ variation_label: e.target.value }); }} />
                  </label>
                  <label className="flex-1 min-w-[200px]">
                    <span className={LABEL}>{t.variationLabelField} (EN)</span>
                    <input key={`lblen-${variations.variation_label_en}`} data-testid="variation-label-en"
                      defaultValue={variations.variation_label_en || ""}
                      placeholder="e.g. Choose a format" className={INPUT}
                      onBlur={(e) => { if (e.target.value !== (variations.variation_label_en || "")) setVarLabel({ variation_label_en: e.target.value }); }} />
                  </label>
                  {variations.variation_group_id ? (
                    <span className="pb-2.5 text-xs text-muted-foreground">{t.variationGroup}: <span className="font-mono text-foreground">#{variations.variation_group_id}</span></span>
                  ) : null}
                </div>

                {variations.variants.length === 0 ? (
                  <div className="border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">{t.noVariants}</div>
                ) : (
                  <div className="overflow-x-auto border border-border">
                    <table className="w-full min-w-[720px] text-sm" data-testid="variations-table">
                      <thead>
                        <tr className="border-b border-border bg-muted text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          <th className="px-3 py-2.5">{t.name}</th>
                          <th className="px-3 py-2.5">{t.productCode}</th>
                          <th className="px-3 py-2.5">{t.formatCol}</th>
                          <th className="px-3 py-2.5">{t.price} ($)</th>
                          <th className="px-3 py-2.5">{t.stock}</th>
                          <th className="px-3 py-2.5">{t.status}</th>
                          <th className="px-3 py-2.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {variations.variants.map((v) => (
                          <VariantRow key={v.product_id} v={v} lang={lang} t={t} productId={productId}
                            onSave={updateVariant} onRemove={removeVariant} onOpen={() => navigate(`/admin/products/${v.product_id}`)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="border border-primary/30 bg-primary/5 p-4">
                  <span className={LABEL}>{t.newVariation}</span>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <label className="block flex-1">
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.productCode} *</span>
                      <input data-testid="new-variant-code" value={newVar.product_code} onChange={(e) => setNewVar({ ...newVar, product_code: e.target.value })}
                        placeholder="ex. 461490-3" className={`${INPUT} font-mono`} />
                    </label>
                    <label className="block flex-1">
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.formatCol}</span>
                      <input data-testid="new-variant-format" value={newVar.variant_value} onChange={(e) => setNewVar({ ...newVar, variant_value: e.target.value })}
                        placeholder={'ex. 21"'} className={INPUT} />
                    </label>
                    <label className="block w-full sm:w-32">
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.price} ($)</span>
                      <input data-testid="new-variant-price" type="number" step="0.01" min="0" value={newVar.price} onChange={(e) => setNewVar({ ...newVar, price: e.target.value })}
                        placeholder="0.00" className={`${INPUT} tabular-nums`} />
                    </label>
                    <button data-testid="new-variant-create" onClick={createVariant} disabled={creatingVar}
                      className="inline-flex items-center justify-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                      <Plus size={15} weight="bold" /> {t.addVariationBtn}
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">{t.newVariationHint}</p>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {showDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-product-modal">
          <div className="w-full max-w-md border border-border bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"><Warning size={22} weight="bold" /></div>
              <div>
                <h2 className="font-display text-lg font-black tracking-tight text-foreground">{t.deleteProductTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.deleteProductWarning}</p>
              </div>
            </div>
            <div className="mb-5 border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-sm font-semibold text-foreground">{p.name?.[lang] || p.name?.fr}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{p.product_code}</div>
            </div>
            <div className="flex justify-end gap-3">
              <button data-testid="delete-product-cancel" onClick={() => setShowDel(false)} disabled={deleting}
                className="border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted disabled:opacity-60">{t.cancel}</button>
              <button data-testid="delete-product-confirm" onClick={deleteProduct} disabled={deleting}
                className="inline-flex items-center gap-2 bg-red-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-red-700 disabled:opacity-60">
                <Trash size={14} weight="bold" /> {t.deletePermanently}
              </button>
            </div>
          </div>
        </div>
      )}

      {delVar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-variant-modal">
          <div className="w-full max-w-md border border-border bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"><Warning size={22} weight="bold" /></div>
              <div>
                <h2 className="font-display text-lg font-black tracking-tight text-foreground">{t.deleteVariationTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.deleteVariationWarning}</p>
              </div>
            </div>
            <div className="mb-5 border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-sm font-semibold text-foreground">{delVar.variant_value || (delVar.name?.[lang] || delVar.name?.fr)}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{delVar.product_code}</div>
            </div>
            <div className="flex justify-end gap-3">
              <button data-testid="delete-variant-cancel" onClick={() => setDelVar(null)} disabled={deletingVar}
                className="border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted disabled:opacity-60">{t.cancel}</button>
              <button data-testid="delete-variant-confirm" onClick={confirmDeleteVariant} disabled={deletingVar}
                className="inline-flex items-center gap-2 bg-red-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-red-700 disabled:opacity-60">
                <Trash size={14} weight="bold" /> {t.deletePermanently}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const VariantRow = ({ v, lang, t, productId, onSave, onRemove, onOpen }) => {
  const [val, setVal] = useState(v.variant_value || "");
  const [code, setCode] = useState(v.product_code || "");
  const [image, setImage] = useState(v.image || "");
  const [price, setPrice] = useState(v.price ?? 0);
  const [qty, setQty] = useState(v.quantity ?? 0);
  const [status, setStatus] = useState(v.status || "A");
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const isBase = v.product_id === productId;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/uploads/image`, fd);
      setImage(data.url);
      toast.success(t.saved);
    } catch (err) { toast.error(err.response?.data?.detail || t.saveFailed); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <tr data-testid={`variant-row-${v.product_id}`} className="border-b border-border">
      <td className="px-3 py-2 font-medium text-foreground">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">
            {image ? <img src={assetSrc(image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={14} className="text-muted-foreground" />}
          </div>
          <div className="min-w-0">
            <span className="block truncate">{v.name?.[lang] || v.name?.fr}{isBase && <span className="ml-1 text-[10px] text-primary">(actuel)</span>}</span>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`variant-image-file-${v.product_id}`} onChange={onUpload} />
            <button type="button" data-testid={`variant-image-upload-${v.product_id}`} onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-primary hover:underline disabled:opacity-50">{uploading ? "…" : t.changeImage}</button>
          </div>
        </div>
      </td>
      <td className="px-3 py-2"><input data-testid={`variant-code-${v.product_id}`} value={code} onChange={(e) => setCode(e.target.value)} className="w-36 border border-border bg-background px-2 py-1.5 font-mono text-[12px] text-primary outline-none focus:border-primary" /></td>
      <td className="px-3 py-2"><input data-testid={`variant-value-${v.product_id}`} value={val} onChange={(e) => setVal(e.target.value)} className="w-20 border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary" /></td>
      <td className="px-3 py-2"><input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="w-24 border border-border bg-background px-2 py-1.5 text-sm tabular-nums outline-none focus:border-primary" /></td>
      <td className="px-3 py-2"><input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="w-20 border border-border bg-background px-2 py-1.5 text-sm tabular-nums outline-none focus:border-primary" /></td>
      <td className="px-3 py-2">
        <select data-testid={`variant-status-${v.product_id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary">
          <option value="A">{t.statusActive}</option>
          <option value="D">{t.statusDisabled}</option>
        </select>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <button data-testid={`variant-save-${v.product_id}`} onClick={() => onSave(v.product_id, { variant_value: val, product_code: code, image, price: parseFloat(price) || 0, quantity: parseInt(qty, 10) || 0, status })}
            className="border border-primary p-1.5 text-primary hover:bg-primary hover:text-white" title={t.save}><FloppyDisk size={14} weight="bold" /></button>
          <button onClick={onOpen} className="border border-border p-1.5 text-muted-foreground hover:text-primary" title="Open"><ArrowSquareOut size={14} weight="bold" /></button>
          {!isBase && <button data-testid={`variant-remove-${v.product_id}`} onClick={() => onRemove(v)} className="border border-red-400 p-1.5 text-red-500 hover:bg-red-500 hover:text-white" title={t.deleteBanner || "Delete"}><Trash size={14} weight="bold" /></button>}
        </div>
      </td>
    </tr>
  );
};
